import { ComponentType } from 'react';
import paymentService from '../../services/paymentService';
import InfiniCareCreditsDialog from './InfiniCareCreditsDialog';
import { PayURedirectRequest } from './PayURedirectForm';

export interface WalletCheckoutMeta {
  txnId: string;
  packName: string;
}

export interface WalletDialogProps {
  open: boolean;
  onClose: () => void;
  onCheckout: (request: PayURedirectRequest, meta: WalletCheckoutMeta) => void;
  returnPath: string;
  balance: number | null;
  onSettled: () => void;
}

export interface WalletProvider {
  key: string;
  serviceSlug: string;
  labelKey: string;
  fetchBalance: () => Promise<number>;
  formatBalance?: (balance: number) => string;
  Dialog: ComponentType<WalletDialogProps>;
}

const PENDING_CHECKOUT_KEY = 'infinicare.wallet.pendingCheckout';

export interface PendingCheckout extends WalletCheckoutMeta {
  returnPath: string;
  startedAt: string;
}

export const readPendingCheckout = (): PendingCheckout | null => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    return parsed && parsed.txnId ? parsed : null;
  } catch {
    return null;
  }
};

export const savePendingCheckout = (record: PendingCheckout) => {
  try {
    window.sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(record));
  } catch {
    // Storage can be unavailable (private mode); the callback still settles the payment.
  }
};

export const clearPendingCheckout = () => {
  try {
    window.sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    // Nothing to clear.
  }
};

export const INFINICARE_WALLET: WalletProvider = {
  key: 'infinicare',
  serviceSlug: '',
  labelKey: 'infinicareCredits',
  fetchBalance: async () => (await paymentService.getMyCredits()).balance,
  Dialog: InfiniCareCreditsDialog,
};

export const WALLET_PROVIDERS: WalletProvider[] = [INFINICARE_WALLET];

export const WALLET_HIDDEN_PREFIXES = ['/admin', '/payment', '/payments', '/checkout', '/payu'];

export const isWalletHidden = (pathname: string): boolean =>
  WALLET_HIDDEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

export const walletProviderForSlug = (slug?: string): WalletProvider =>
  WALLET_PROVIDERS.find((provider) => provider.serviceSlug && provider.serviceSlug === slug) ??
  INFINICARE_WALLET;
